import { useCallback, useEffect, useRef, useTransition } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useModal } from "../../context/ModalProvider";
import { useEvents } from "../../hooks/useEvents";
import { useWizardFinish } from "../Planner/hooks/useWizardFinish";
import { useJourneys } from "../JourneyRail/useJourneys";
import { plannerHref } from "../../lib/navPaths";
import { TrainingPlanWizard } from "../Planner/TrainingPlanWizard";
import type { OnboardingEntry } from "../../lib/onboardingAnalytics";

/**
 * The two landing "door" actions, shared by every on-ramp that opens them
 * (the editorial landing CTAs and the sweepstakes CTA), so there is ONE source
 * of truth for what each door does.
 *
 *  - `onApplyPlan`  → opens the {@link TrainingPlanWizard}. The shared event
 *    funnel (`useEventSetup.applyWizardPlan`, journeys overhaul P2) owns the
 *    profile-first PATCH + journey create + anon race draft; here we only keep
 *    the shared finish (`useWizardFinish`: toast, planner refresh, the
 *    training-method handoff, then My Fuel or the calendar). A returning
 *    athlete with an ACTIVE journey (race or
 *    rolling) never re-enters the wizard here — they go straight to My Fuel
 *    (D8).
 *  - `onManual`     → navigates straight to the workout planner (`/my-training`;
 *    the "I'm just training" door + "Open the planner" CTAs). No upfront
 *    onboarding: a fresh visitor lands on the calendar and starts adding
 *    sessions, and the about-me / fuel profile is collected later on My Fuel —
 *    whose setup CTAs open the same wizard on demand.
 */
export function useLandingDoorActions(): {
  onApplyPlan: () => void;
  onManual: () => void;
  /** True while the planner route (lazy chunk) is loading after an `onManual` click. */
  manualPending: boolean;
} {
  const navigate = useNavigate();
  const { open: openModal, close: closeModal } = useModal();
  const { events, loading: eventsLoading, create: createEvent } = useEvents();
  const location = useLocation();

  // The principal's active journeys (customers AND anon sessions own them),
  // read while the athlete is on the landing page — the shared, principal-keyed
  // snapshot, so the wizard opened from here also paints from it. Drives the
  // D8 shortcut below.
  const { journeys: activeJourneys, loading: journeysLoading } = useJourneys();
  const hasActiveJourney = !journeysLoading && activeJourneys.length > 0;

  // "Open the planner" — go straight to the workout planner. It's calendar-first
  // for everyone (journey or not), so a fresh anon visitor lands on an editable
  // calendar with nothing to fill in first.
  //
  // The planner screen is a lazy route chunk, and React keeps the landing on
  // screen while it loads (router navigations are transitions), so a cold click
  // could sit for seconds with zero feedback. Navigating inside our own
  // transition surfaces that wait as `manualPending` so the CTAs can show a
  // loading state.
  const [manualPending, startPlannerNav] = useTransition();
  const openPlanner = useCallback(() => {
    startPlannerNav(() => {
      navigate(plannerHref());
    });
  }, [navigate]);

  // Both wizard finishes — the shared host behaviour (`useWizardFinish`), so
  // every host lands the athlete the same way.
  const { onApply, onRollingCreated } = useWizardFinish();

  // D8: a returning athlete with an active journey goes straight to My Fuel.
  // While journeys are still loading the click is never blocked — the wizard
  // opens as before. `entry` is funnel analytics only.
  const openWizard = useCallback((entry: OnboardingEntry) => {
    if (hasActiveJourney) {
      navigate("/my-fuel");
      return;
    }
    const id = openModal(
      <TrainingPlanWizard
        onClose={() => closeModal(id)}
        onApply={onApply}
        onRollingCreated={onRollingCreated}
        savedEvents={events}
        createEvent={createEvent}
        entry={entry}
      />
    );
  }, [hasActiveJourney, navigate, openModal, closeModal, onApply, onRollingCreated, events, createEvent]);
  const onApplyPlan = useCallback(() => openWizard("landing"), [openWizard]);

  // `?welcome=1` — the storefront launcher's real-navigation fallback
  // (`<proxy>/welcome` 301s here) and any other deep link that should land
  // with the wizard already open. Consumed ONCE per arrival, after the saved
  // events AND active journeys have loaded (so a returning athlete's races are
  // in the wizard, and one with an active journey lands on My Fuel instead —
  // D8 via `openWizard`), and stripped from the URL so refresh/back doesn't
  // reopen it.
  const welcomeHandledRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("welcome") !== "1") {
      welcomeHandledRef.current = false;
      return;
    }
    if (welcomeHandledRef.current || eventsLoading || journeysLoading) return;
    welcomeHandledRef.current = true;
    params.delete("welcome");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true }
    );
    openWizard("deep_link");
  }, [location, eventsLoading, journeysLoading, navigate, openWizard]);

  return { onApplyPlan, onManual: openPlanner, manualPending };
}
